import React, { useEffect, useRef, useState } from 'react';
import { BookOpen, Check, ChevronRight, Loader2 } from 'lucide-react';

import { lookupKangxiCharApi } from '../lib/api';
import {
  CandidateNameChar,
  getCandidateCharsByStroke,
  getNamingGridGenderFit,
  lookupLocalSurnameStroke,
  NamingGender,
  recommendStrokeCombos,
  StrokeComboRecommendation,
} from '../lib/naming-master';
import { calculateSancaiLiuge } from '../lib/sancai-liuge';
import XuanYuanTopBar from './XuanYuanTopBar';

interface NamingMasterAppProps {
  onBack: () => void;
  initialSurname?: string;
  initialComboKey?: string;
  initialView?: 'combos' | 'candidates' | 'saved';
  initialGender?: NamingGender;
  onOpenKangxiDictionary?: (char: string, surname?: string, comboKey?: string, gender?: NamingGender, view?: PageMode) => void;
  onOpenSancaiLiuge?: (payload: { surname: string; givenName: string; gender: NamingGender }) => void;
}

interface ResolvedSurnameState {
  surname: string;
  stroke: number;
  traditional: string;
  pinyin: string;
  sourceLabel: string;
  sourceDetail: string;
}

interface SavedNameItem {
  id: string;
  gender: NamingGender;
  surname: string;
  fullName: string;
  secondChar: CandidateNameChar;
  thirdChar: CandidateNameChar;
  combo: StrokeComboRecommendation;
  createdAt: number;
}

interface NamingHistoryItem {
  surname: string;
  names: string[];
  timestamp: number;
}

interface HistoryNameDetail {
  fullName: string;
  givenName: string;
  score: number | null;
  luckText: string;
}

const KAITI_FONT = "font-['Kaiti','STKaiti','KaiTi_GB2312','楷体','serif']";
const COMBO_PREVIEW_LIMIT = 18;
const CANDIDATE_PREVIEW_LIMIT = 160;
const HAN_CHAR_RE = /[\u3400-\u9fff]/g;
const HISTORY_KEY = 'naming_master_history_v2';
const MAX_HISTORY = 10;

const COLORS = {
  primary: {
    50: '#FDF6F0',
    100: '#F9EBE0',
    200: '#F0D4C0',
    300: '#E5B89A',
    400: '#D69A75',
    500: '#C77B40',
    600: '#A65D2E',
    700: '#854A24',
    800: '#6B3D1F',
    900: '#4D2E19',
  },
  neutral: {
    50: '#FDFCFB',
    100: '#F7F4F1',
    200: '#EDE8E3',
    300: '#DDD6CE',
    400: '#B8A99C',
    500: '#9A8779',
    600: '#7D6B5E',
    700: '#655648',
    800: '#4A3F35',
    900: '#3D332B',
  },
  accent: {
    gold: '#B8956B',
    jade: '#5A8A6E',
    crimson: '#A65D5D',
    ink: '#2C241B',
  },
  score: {
    excellent: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
    good: { bg: 'bg-sky-50', text: 'text-sky-700', border: 'border-sky-200' },
    average: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
    poor: { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200' },
  },
} as const;

const COMBO_GRID_ORDER = [
  { key: 'tiange', label: '天格' },
  { key: 'renge', label: '人格' },
  { key: 'dige', label: '地格' },
  { key: 'waige', label: '外格' },
  { key: 'biange', label: '变格' },
  { key: 'zongge', label: '总格' },
] as const;

type PageMode = 'search' | 'combos' | 'candidates' | 'saved';

const sanitizeSurname = (raw: string): string => {
  const chars = String(raw || '').match(HAN_CHAR_RE) || [];
  return chars.slice(0, 1).join('');
};

const sourceLabelFromApi = (source: string): string => {
  switch (source) {
    case 'nameology':
      return '姓名学字库';
    case 'd1':
      return '云端姓名学字库';
    case 'zdic':
      return '在线字典兜底';
    case 'local':
    case 'packed':
      return '本地姓名学字库';
    case 'modern':
      return '本地补充字库';
    case 'fallback':
      return '补充映射';
    default:
      return '本地回退';
  }
};

const gridLabelClass = (score: number): string => {
  if (score >= 90) return 'bg-emerald-100 text-emerald-700 border-emerald-200';
  if (score >= 80) return 'bg-sky-100 text-sky-700 border-sky-200';
  if (score >= 70) return 'bg-amber-100 text-amber-700 border-amber-200';
  return 'bg-rose-100 text-rose-700 border-rose-200';
};

const stageBadgeClass = (level: string): string => {
  if (level === '大吉') return 'bg-emerald-100 text-emerald-700 border-emerald-200';
  if (level === '吉') return 'bg-sky-100 text-sky-700 border-sky-200';
  if (level === '平') return 'bg-stone-100 text-stone-700 border-stone-200';
  if (level === '小凶') return 'bg-amber-100 text-amber-700 border-amber-200';
  return 'bg-rose-100 text-rose-700 border-rose-200';
};

const getInitialPage = (
  initialSurname?: string,
  initialView?: 'combos' | 'candidates' | 'saved',
): PageMode => {
  if (initialView === 'saved') return 'saved';
  const cleanSurname = sanitizeSurname(initialSurname || '');
  if (!cleanSurname) return 'search';
  return initialView === 'candidates' ? 'candidates' : 'combos';
};

const getInitialGender = (raw?: string): NamingGender => (raw === 'female' ? 'female' : 'male');

const getGenderLabel = (gender: NamingGender): string => (gender === 'female' ? '女名' : '男名');

const formatGridNumber = (combo: StrokeComboRecommendation['grids'][typeof COMBO_GRID_ORDER[number]['key']]): string => (
  combo.rawValue === combo.value81 ? String(combo.value81) : `${combo.rawValue}→${combo.value81}`
);

const getGenderLuckColor = (genderLuck: string, gender: NamingGender): { border: string; bg: string; text: string } => {
  const fit = getNamingGridGenderFit(genderLuck, gender);
  if (fit === 'good') {
    return { border: '#D1FAE5', bg: '#ECFDF5', text: '#047857' };
  }
  if (fit === 'bad') {
    return { border: '#FECACA', bg: '#FEF2F2', text: '#B91C1C' };
  }
  return { border: '#FED7AA', bg: '#FFF7ED', text: '#C2410C' };
};

const formatNamingMasterGenderLuck = (genderLuck: string): string => {
  const compact = String(genderLuck || '').replace(/\s+/g, '').trim();
  if (!compact) return '';
  if (compact.startsWith('男女皆') || compact.startsWith('男女均')) {
    return compact.replace(/^男女[皆均]/, '').trim();
  }
  return compact;
};

const buildHistoryLuckText = (combo: StrokeComboRecommendation): string => (
  [
    ['人格', combo.grids.renge],
    ['地格', combo.grids.dige],
    ['总格', combo.grids.zongge],
  ].map(([label, grid]) => `${label}${formatGridNumber(grid)} ${formatNamingMasterGenderLuck(grid.genderLuck) || grid.chartLuck}`).join(' · ')
);

const normalizeHistoryEntry = (raw: unknown): NamingHistoryItem | null => {
  if (typeof raw === 'string') {
    const surname = sanitizeSurname(raw);
    return surname ? { surname, names: [], timestamp: 0 } : null;
  }
  if (!raw || typeof raw !== 'object') return null;
  const data = raw as { surname?: unknown; names?: unknown; timestamp?: unknown };
  const surname = sanitizeSurname(String(data.surname || ''));
  if (!surname) return null;
  const names = Array.isArray(data.names)
    ? data.names.map((item) => String(item || '').trim()).filter(Boolean)
    : [];
  const timestamp = Number(data.timestamp);
  return {
    surname,
    names,
    timestamp: Number.isFinite(timestamp) ? timestamp : 0,
  };
};

const mergeHistoryEntries = (items: NamingHistoryItem[]): NamingHistoryItem[] => {
  const merged = new Map<string, NamingHistoryItem>();
  items.forEach((item) => {
    const current = merged.get(item.surname);
    if (!current) {
      merged.set(item.surname, {
        surname: item.surname,
        names: [...item.names],
        timestamp: item.timestamp,
      });
      return;
    }
    merged.set(item.surname, {
      surname: item.surname,
      names: [...current.names, ...item.names].filter((name, index, list) => list.indexOf(name) === index),
      timestamp: Math.max(current.timestamp, item.timestamp),
    });
  });
  return Array.from(merged.values()).sort((a, b) => b.timestamp - a.timestamp).slice(0, MAX_HISTORY);
};

const GenderToggle: React.FC<{
  value: NamingGender;
  onChange: (value: NamingGender) => void;
}> = ({ value, onChange }) => (
  <div className="inline-flex rounded-full border p-1" style={{ borderColor: COLORS.neutral[200], backgroundColor: 'white' }}>
    {([
      { key: 'male', label: '男名' },
      { key: 'female', label: '女名' },
    ] as const).map((item) => {
      const active = value === item.key;
      return (
        <button
          key={item.key}
          type="button"
          onClick={() => onChange(item.key)}
          className="rounded-full px-4 py-2 text-sm font-medium transition-all duration-200"
          style={{
            backgroundColor: active ? COLORS.primary[500] : 'transparent',
            color: active ? '#fff' : COLORS.neutral[700],
            boxShadow: active ? '0 6px 16px rgba(166, 93, 46, 0.24)' : undefined,
          }}
        >
          {item.label}
        </button>
      );
    })}
  </div>
);

const SixGridInline: React.FC<{
  combo: StrokeComboRecommendation;
}> = ({ combo }) => (
  <div className="mt-2 text-[11px] leading-5 text-[#846B5A] sm:text-xs sm:leading-6">
    {COMBO_GRID_ORDER.map(({ key, label }) => {
      const grid = combo.grids[key];
      return `${label}${grid.rawValue}${grid.luckLevel}`;
    }).join(' - ')}
  </div>
);

const CandidateGrid: React.FC<{
  title: string;
  stroke: number;
  total: number;
  candidates: CandidateNameChar[];
  showAll: boolean;
  onToggleShowAll: () => void;
  surname?: string;
  comboKey?: string;
  gender?: NamingGender;
  view?: PageMode;
  onOpenKangxiDictionary?: (char: string, surname?: string, comboKey?: string, gender?: NamingGender, view?: PageMode) => void;
  selectedChar?: string;
  onSelectChar?: (item: CandidateNameChar) => void;
}> = ({
  title,
  stroke,
  total,
  candidates,
  showAll,
  onToggleShowAll,
  surname,
  comboKey,
  gender,
  view,
  onOpenKangxiDictionary,
  selectedChar,
  onSelectChar,
}) => {
  const lastTapRef = useRef<{ char: string; timestamp: number } | null>(null);

  const handleCandidateTap = (item: CandidateNameChar) => {
    const now = Date.now();
    const isDoubleTap = Boolean(
      onOpenKangxiDictionary
      && lastTapRef.current
      && lastTapRef.current.char === item.char
      && now - lastTapRef.current.timestamp <= 280
    );
    if (isDoubleTap) {
      lastTapRef.current = null;
      onOpenKangxiDictionary?.(item.char, surname, comboKey, gender, view);
      return;
    }
    lastTapRef.current = { char: item.char, timestamp: now };
    onSelectChar?.(item);
  };

  return (
    <div className="overflow-hidden rounded-2xl border bg-white p-4 shadow-[0_4px_16px_rgba(107,61,31,0.05)] sm:rounded-3xl sm:p-5" style={{ borderColor: COLORS.neutral[200] }}>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className={`text-lg font-semibold sm:text-xl`} style={{ color: COLORS.neutral[800], fontFamily: "'Kaiti','STKaiti','楷体',serif" }}>
            {title}
          </h3>
          <p className="mt-1 text-xs sm:text-sm" style={{ color: COLORS.neutral[500] }}>
            {stroke} 画候选字 · 已过滤生僻字
          </p>
          {onOpenKangxiDictionary ? (
            <p className="mt-1 text-[11px] sm:text-xs" style={{ color: COLORS.primary[600] }}>
              单击选字，双击进入《康熙字典》
            </p>
          ) : null}
        </div>
        {total > CANDIDATE_PREVIEW_LIMIT ? (
          <button
            type="button"
            onClick={onToggleShowAll}
            className="rounded-full border px-4 py-1.5 text-xs font-medium transition-all duration-200 hover:shadow-md"
            style={{
              borderColor: COLORS.primary[200],
              backgroundColor: COLORS.primary[50],
              color: COLORS.primary[700],
            }}
          >
            {showAll ? '收起' : `查看更多 (${total})`}
          </button>
        ) : null}
      </div>
      <div className="grid grid-cols-5 gap-2 sm:grid-cols-6 md:grid-cols-7 lg:grid-cols-8">
        {candidates.map((item) => (
          <button
            key={`${title}-${item.char}`}
            type="button"
            onClick={() => handleCandidateTap(item)}
            className="group relative overflow-hidden rounded-xl border px-2 py-2 text-center transition-all duration-200 hover:shadow-sm sm:rounded-2xl sm:px-2.5 sm:py-3"
            style={{
              borderColor: selectedChar === item.char ? COLORS.primary[400] : COLORS.neutral[200],
              backgroundColor: selectedChar === item.char ? COLORS.primary[50] : 'white',
              cursor: onSelectChar ? 'pointer' : 'default',
              boxShadow: selectedChar === item.char ? `0 6px 16px ${COLORS.primary[200]}40` : undefined,
            }}
          >
            {selectedChar === item.char ? (
              <div className="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full" style={{ backgroundColor: COLORS.primary[500], color: '#fff' }}>
                <Check className="h-3.5 w-3.5" />
              </div>
            ) : null}
            <div
              className={`text-xl font-bold sm:text-2xl ${selectedChar === item.char ? 'scale-105' : ''}`}
              style={{ color: COLORS.neutral[800], fontFamily: "'Kaiti','STKaiti','楷体',serif" }}
            >
              {item.char}
            </div>
            <div className="mt-1 text-[10px] sm:text-xs" style={{ color: COLORS.neutral[500] }}>
              {item.stroke}画
            </div>
            <div className="mt-0.5 text-[10px] font-medium sm:text-xs" style={{ color: COLORS.primary[600] }}>
              {item.wuxing}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

const NamingMasterApp: React.FC<NamingMasterAppProps> = ({
  onBack,
  initialSurname,
  initialComboKey,
  initialView,
  initialGender,
  onOpenKangxiDictionary,
  onOpenSancaiLiuge,
}) => {
  const [surname, setSurname] = useState(() => sanitizeSurname(initialSurname || ''));
  const [gender, setGender] = useState<NamingGender>(() => getInitialGender(initialGender));
  const [resolvedSurname, setResolvedSurname] = useState<ResolvedSurnameState | null>(null);
  const [comboList, setComboList] = useState<StrokeComboRecommendation[]>([]);
  const [selectedComboKey, setSelectedComboKey] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showAllSecondChars, setShowAllSecondChars] = useState(false);
  const [showAllThirdChars, setShowAllThirdChars] = useState(false);
  const [page, setPage] = useState<PageMode>(() => getInitialPage(initialSurname, initialView));
  const [history, setHistory] = useState<NamingHistoryItem[]>([]);
  const [selectedSecondChar, setSelectedSecondChar] = useState<CandidateNameChar | null>(null);
  const [selectedThirdChar, setSelectedThirdChar] = useState<CandidateNameChar | null>(null);
  const [savedNames, setSavedNames] = useState<SavedNameItem[]>([]);
  const [saveToast, setSaveToast] = useState('');
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [currentHistoryDetails, setCurrentHistoryDetails] = useState<HistoryNameDetail[]>([]);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(HISTORY_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setHistory(mergeHistoryEntries(parsed.map(normalizeHistoryEntry).filter(Boolean) as NamingHistoryItem[]));
        }
      }
    } catch {
      setHistory([]);
    }
  }, []);

  const saveToHistory = (surnameValue: string, fullName?: string) => {
    if (!surnameValue) return;
    try {
      const stored = localStorage.getItem(HISTORY_KEY);
      let parsed: NamingHistoryItem[] = [];
      if (stored) {
        const raw = JSON.parse(stored);
        if (Array.isArray(raw)) {
          parsed = mergeHistoryEntries(raw.map(normalizeHistoryEntry).filter(Boolean) as NamingHistoryItem[]);
        }
      }
      const existing = parsed.find((item) => item.surname === surnameValue);
      const names = [
        String(fullName || '').trim(),
        ...(existing?.names || []),
      ].filter(Boolean).filter((item, index, list) => list.indexOf(item) === index);
      const filtered = parsed.filter((item) => item.surname !== surnameValue);
      const newHistory = mergeHistoryEntries([{ surname: surnameValue, names, timestamp: Date.now() }, ...filtered]);
      localStorage.setItem(HISTORY_KEY, JSON.stringify(newHistory));
      setHistory(newHistory);
    } catch {
      return;
    }
  };

  const selectedCombo = comboList.find((item) => item.key === selectedComboKey) || comboList[0] || null;
  const secondCandidatesAll = selectedCombo ? getCandidateCharsByStroke(selectedCombo.secondStroke) : [];
  const thirdCandidatesAll = selectedCombo ? getCandidateCharsByStroke(selectedCombo.thirdStroke) : [];
  const secondCandidates = showAllSecondChars ? secondCandidatesAll : secondCandidatesAll.slice(0, CANDIDATE_PREVIEW_LIMIT);
  const thirdCandidates = showAllThirdChars ? thirdCandidatesAll : thirdCandidatesAll.slice(0, CANDIDATE_PREVIEW_LIMIT);
  const sortedSavedNames = [...savedNames].sort((a, b) => b.combo.score - a.combo.score || b.createdAt - a.createdAt);
  const currentHistoryNames = resolvedSurname
    ? (history.find((item) => item.surname === resolvedSurname.surname)?.names || [])
    : [];

  useEffect(() => {
    setHistoryExpanded(false);
  }, [resolvedSurname?.surname, page]);

  useEffect(() => {
    if (!resolvedSurname || currentHistoryNames.length === 0) {
      setCurrentHistoryDetails([]);
      return;
    }

    const comboLookup = new Map(
      recommendStrokeCombos({
        surnameStroke: resolvedSurname.stroke,
        gender,
        limit: 900,
      }).map((item) => [item.key, item]),
    );

    const details = currentHistoryNames
      .map((fullName) => {
        const chars = Array.from(String(fullName || '').trim());
        const givenName = chars.slice(1, 3).join('');
        if (givenName.length !== 2) return null;
        const result = calculateSancaiLiuge({
          surname: resolvedSurname.surname,
          givenName,
          gender,
        }).result;
        const givenSlots = result?.strokeSlots.filter((slot) => slot.part === 'given') || [];
        const comboKey = givenSlots.length >= 2 ? `${givenSlots[0].finalStroke}-${givenSlots[1].finalStroke}` : '';
        const combo = comboKey ? comboLookup.get(comboKey) || null : null;
        return {
          fullName: `${resolvedSurname.surname}${givenName}`,
          givenName,
          score: combo?.score ?? null,
          luckText: combo ? buildHistoryLuckText(combo) : '吉凶待重新计算',
        };
      })
      .filter(Boolean)
      .sort((a, b) => (b?.score || 0) - (a?.score || 0) || String(a?.fullName || '').localeCompare(String(b?.fullName || ''), 'zh-Hans-CN')) as HistoryNameDetail[];

    setCurrentHistoryDetails(details);
  }, [resolvedSurname, currentHistoryNames, gender]);

  const handleSelectSecondChar = (item: CandidateNameChar) => {
    setSelectedSecondChar((current) => (current?.char === item.char ? null : item));
  };

  const handleSelectThirdChar = (item: CandidateNameChar) => {
    setSelectedThirdChar((current) => (current?.char === item.char ? null : item));
  };

  const handleSaveComposedName = () => {
    if (!resolvedSurname || !selectedCombo || !selectedSecondChar || !selectedThirdChar) return;
    const secondChar = selectedSecondChar;
    const thirdChar = selectedThirdChar;
    const fullName = `${resolvedSurname.surname}${selectedSecondChar.char}${selectedThirdChar.char}`;
    const nextItem: SavedNameItem = {
      id: `${gender}-${selectedCombo.key}-${selectedSecondChar.char}-${selectedThirdChar.char}`,
      gender,
      surname: resolvedSurname.surname,
      fullName,
      secondChar,
      thirdChar,
      combo: selectedCombo,
      createdAt: Date.now(),
    };
    setSelectedSecondChar(null);
    setSelectedThirdChar(null);
    setSavedNames((current) => {
      const filtered = current.filter((item) => item.id !== nextItem.id);
      return [nextItem, ...filtered];
    });
    saveToHistory(resolvedSurname.surname, fullName);
    setSaveToast(`已保存：${fullName}`);
  };

  useEffect(() => {
    if (!saveToast) return;
    const timer = window.setTimeout(() => setSaveToast(''), 1800);
    return () => window.clearTimeout(timer);
  }, [saveToast]);

  const runAnalysis = async (
    rawSurname?: string,
    preferredComboKey?: string,
    nextPage: Extract<PageMode, 'combos' | 'candidates'> = 'combos',
    nextGender: NamingGender = gender,
  ) => {
    const cleanSurname = sanitizeSurname(rawSurname ?? surname);
    if (!cleanSurname) {
      setError('请输入一个中文姓氏。当前仅支持单姓双字名。');
      setResolvedSurname(null);
      setComboList([]);
      setSelectedComboKey('');
      setPage('search');
      return;
    }

    setIsLoading(true);
    setError('');
    setShowAllSecondChars(false);
    setShowAllThirdChars(false);

    try {
      let stroke = 0;
      let traditional = '';
      let pinyin = '';
      let sourceLabel = '';
      let sourceDetail = '';

      try {
        const apiResult = await lookupKangxiCharApi(cleanSurname);
        stroke = apiResult.stroke;
        traditional = apiResult.traditional || cleanSurname;
        pinyin = apiResult.pinyin || '';
        sourceLabel = '联网姓名学查询';
        sourceDetail = sourceLabelFromApi(apiResult.source);
      } catch {
        const localResult = lookupLocalSurnameStroke(cleanSurname);
        stroke = localResult.stroke;
        traditional = localResult.traditional || cleanSurname;
        sourceLabel = '本地姓名学字库';
        sourceDetail = localResult.source === 'fallback'
          ? '补充映射'
          : localResult.source === 'nameology'
            ? '姓名学字库'
            : '离线字库';
      }

      if (!(stroke > 0)) {
        throw new Error('暂未查到该姓氏的有效姓名学笔画，请换一个姓氏再试。');
      }

      const combos = recommendStrokeCombos({
        surnameStroke: stroke,
        gender: nextGender,
        limit: COMBO_PREVIEW_LIMIT,
      });

      if (combos.length === 0) {
        throw new Error('当前字库里没有可用的双字组合，请稍后再试。');
      }

      const matchedCombo = combos.find((item) => item.key === String(preferredComboKey || '').trim()) || combos[0];
      setResolvedSurname({
        surname: cleanSurname,
        stroke,
        traditional,
        pinyin,
        sourceLabel,
        sourceDetail,
      });
      setComboList(combos);
      setSelectedComboKey(matchedCombo.key);
      setPage(nextPage);
      setSelectedSecondChar(null);
      setSelectedThirdChar(null);
      saveToHistory(cleanSurname);
    } catch (err) {
      setResolvedSurname(null);
      setComboList([]);
      setSelectedComboKey('');
      setError(err instanceof Error ? err.message : '生成起名组合失败，请稍后重试。');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const clean = sanitizeSurname(initialSurname || '');
    const normalizedGender = getInitialGender(initialGender);
    setGender(normalizedGender);
    if (!clean) {
      setPage('search');
      return;
    }
    setSurname(clean);
    void runAnalysis(clean, initialComboKey, initialView === 'candidates' ? 'candidates' : 'combos', normalizedGender);
  }, [initialSurname, initialComboKey, initialView, initialGender]);

  const handleGenderChange = (nextGender: NamingGender) => {
    if (nextGender === gender) return;
    setGender(nextGender);
    if (!resolvedSurname) return;
    void runAnalysis(
      resolvedSurname.surname,
      selectedComboKey,
      page === 'candidates' ? 'candidates' : 'combos',
      nextGender,
    );
  };

  const handleTopBack = () => {
    if (page === 'saved') {
      setPage('combos');
      return;
    }
    if (page === 'candidates') {
      setPage('combos');
      return;
    }
    if (page === 'combos' && resolvedSurname) {
      setPage('search');
      return;
    }
    onBack();
  };

  const showSearchPage = page === 'search';
  const showComboListPage = page === 'combos';
  const showCandidatePage = page === 'candidates';
  const showSavedPage = page === 'saved';

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FDF8F3] via-[#F8F0E8] to-[#F5EBE0]" style={{ color: COLORS.neutral[900] }}>
      <XuanYuanTopBar
        title="起名大师"
        onBack={handleTopBack}
        variant="light"
        maxWidthClassName="max-w-6xl"
      />

      <div className="mx-auto max-w-6xl px-4 pb-12 pt-6 sm:px-6 sm:pb-16 sm:pt-8">
        {showSearchPage ? (
          <>
            <section className="mb-6">
              <div className="mb-6 text-center">
                <h2 className={`mb-2 text-xl font-semibold sm:text-2xl`} style={{ color: COLORS.neutral[800], fontFamily: "'Kaiti','STKaiti','楷体',serif" }}>
                  输入姓氏开始起名
                </h2>
                <p className="mx-auto max-w-lg text-sm leading-relaxed" style={{ color: COLORS.neutral[600] }}>
                  本页按姓名学笔画口径处理姓氏，并按 {getGenderLabel(gender)} 口径推荐双字名组合
                </p>
              </div>
              <div className="mb-4 flex justify-center">
                <GenderToggle value={gender} onChange={handleGenderChange} />
              </div>
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  void runAnalysis(undefined, undefined, 'combos', gender);
                }}
                className="mx-auto max-w-2xl"
              >
                <div className="flex items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="relative">
                      <input
                        value={surname}
                        onChange={(event) => {
                          setSurname(event.target.value);
                          setError('');
                        }}
                        onCompositionEnd={(event) => {
                          setSurname(sanitizeSurname(event.currentTarget.value));
                        }}
                        onBlur={(event) => {
                          setSurname(sanitizeSurname(event.currentTarget.value));
                        }}
                        placeholder="请输入姓氏"
                        className={`h-12 w-full rounded-xl border-2 px-4 text-lg outline-none transition-all duration-200 focus:border-[${COLORS.primary[400]}] sm:h-14 sm:text-xl ${KAITI_FONT}`}
                        style={{ 
                          borderColor: COLORS.neutral[200], 
                          backgroundColor: 'white',
                          color: COLORS.neutral[800]
                        }}
                      />
                      <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                        <span className="text-xs" style={{ color: COLORS.neutral[400] }}>单姓</span>
                      </div>
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="inline-flex h-12 shrink-0 items-center justify-center rounded-xl px-6 text-sm font-medium text-white shadow-lg transition-all duration-200 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-60 sm:h-14 sm:px-8 sm:text-base"
                    style={{ 
                      background: `linear-gradient(135deg, ${COLORS.primary[600]} 0%, ${COLORS.primary[500]} 50%, ${COLORS.primary[400]} 100%)`,
                      boxShadow: '0 8px 24px rgba(166, 93, 46, 0.3)'
                    }}
                  >
                    {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : '开始匹配'}
                  </button>
                </div>
                {error ? (
                  <div className="mt-4 rounded-xl border px-4 py-3 text-sm" style={{ borderColor: '#FECDD3', backgroundColor: '#FFF1F2', color: '#E11D48' }}>
                    {error}
                  </div>
                ) : null}
              </form>
              
              {history.length > 0 && (
                <div className="mx-auto mt-6 max-w-2xl">
                  <div className="mb-2 text-sm" style={{ color: COLORS.neutral[500] }}>历史记录</div>
                  <div className="flex flex-wrap gap-2">
                    {history.map((item) => (
                      <button
                        key={item.surname}
                        type="button"
                        onClick={() => {
                          setSurname(item.surname);
                          void runAnalysis(item.surname, undefined, 'combos');
                        }}
                        className="rounded-full border px-3 py-1.5 text-sm transition-all duration-200 hover:shadow-md"
                        style={{ 
                          borderColor: COLORS.neutral[200], 
                          backgroundColor: 'white',
                          color: COLORS.neutral[700]
                        }}
                      >
                        <span className="block text-left font-medium">{item.surname}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </section>

            <section>
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="rounded-xl border p-4 text-center" style={{ borderColor: COLORS.neutral[200], backgroundColor: 'white' }}>
                  <div className="mb-1 text-2xl">📚</div>
                  <div className="text-sm font-medium" style={{ color: COLORS.neutral[700] }}>姓名学字库</div>
                  <div className="text-xs" style={{ color: COLORS.neutral[500] }}>简体/繁体/姓名学三口径</div>
                </div>
                <div className="rounded-xl border p-4 text-center" style={{ borderColor: COLORS.neutral[200], backgroundColor: 'white' }}>
                  <div className="mb-1 text-2xl">✨</div>
                  <div className="text-sm font-medium" style={{ color: COLORS.neutral[700] }}>三才五格</div>
                  <div className="text-xs" style={{ color: COLORS.neutral[500] }}>科学姓名学分析</div>
                </div>
                <div className="rounded-xl border p-4 text-center" style={{ borderColor: COLORS.neutral[200], backgroundColor: 'white' }}>
                  <div className="mb-1 text-2xl">🎯</div>
                  <div className="text-sm font-medium" style={{ color: COLORS.neutral[700] }}>智能推荐</div>
                  <div className="text-xs" style={{ color: COLORS.neutral[500] }}>精选候选字组合</div>
                </div>
              </div>
            </section>
          </>
        ) : null}

        {resolvedSurname && showComboListPage ? (
          <section className="mb-4 bg-transparent p-0 shadow-none sm:mb-5" style={{ borderColor: 'transparent' }}>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                <div className={`text-3xl font-bold sm:text-4xl`} style={{ color: COLORS.neutral[800], fontFamily: "'Kaiti','STKaiti','楷体',serif" }}>
                  {resolvedSurname.surname}
                </div>
                <GenderToggle value={gender} onChange={handleGenderChange} />
              </div>
              <div className="flex flex-wrap gap-2">
                <span className="rounded-full border px-3 py-1 text-xs" style={{ borderColor: COLORS.primary[200], backgroundColor: COLORS.primary[50], color: COLORS.primary[700] }}>
                  {resolvedSurname.sourceDetail}
                </span>
              </div>
            </div>
            <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs sm:text-sm" style={{ color: COLORS.neutral[500] }}>
              <span>繁体：{resolvedSurname.traditional || resolvedSurname.surname}</span>
              {resolvedSurname.pinyin ? <span>· 拼音：{resolvedSurname.pinyin}</span> : null}
            </div>
          </section>
        ) : null}

        {resolvedSurname && !showSearchPage && currentHistoryNames.length > 0 ? (
          <section className="mb-5">
            <button
              type="button"
              onClick={() => setHistoryExpanded((value) => !value)}
              className="inline-flex items-center gap-2 rounded-full border bg-white px-4 py-2 text-sm font-medium shadow-[0_4px_16px_rgba(107,61,31,0.05)] transition-all duration-200 hover:shadow-[0_8px_20px_rgba(107,61,31,0.08)]"
              style={{ borderColor: COLORS.neutral[200], color: COLORS.neutral[700] }}
            >
              <span>{`已选名字 (${currentHistoryNames.length})`}</span>
              <ChevronRight className={`h-4 w-4 transition-transform duration-200 ${historyExpanded ? 'rotate-90' : ''}`} />
            </button>
            {historyExpanded ? (
              <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {currentHistoryDetails.map((item) => (
                  <button
                    key={`${resolvedSurname.surname}-${item.fullName}`}
                    type="button"
                    onClick={() => onOpenSancaiLiuge?.({
                      surname: resolvedSurname.surname,
                      givenName: item.givenName,
                      gender,
                    })}
                    className="rounded-2xl border bg-white p-4 text-left shadow-[0_4px_16px_rgba(107,61,31,0.05)] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_10px_24px_rgba(107,61,31,0.10)] sm:rounded-3xl"
                    style={{ borderColor: COLORS.neutral[200] }}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className={`text-xl font-semibold sm:text-2xl`} style={{ color: COLORS.neutral[800], fontFamily: "'Kaiti','STKaiti','楷体',serif" }}>
                        {item.fullName}
                      </div>
                      {item.score !== null ? (
                        <span
                          className="rounded-full border px-3 py-1 text-xs font-semibold"
                          style={{
                            borderColor: item.score >= 90 ? '#D1FAE5' : item.score >= 80 ? '#BAE6FD' : item.score >= 70 ? '#FED7AA' : '#FECACA',
                            backgroundColor: item.score >= 90 ? '#ECFDF5' : item.score >= 80 ? '#E0F2FE' : item.score >= 70 ? '#FFF7ED' : '#FEF2F2',
                            color: item.score >= 90 ? '#059669' : item.score >= 80 ? '#0284C7' : item.score >= 70 ? '#EA580C' : '#DC2626',
                          }}
                        >
                          {item.score}分
                        </span>
                      ) : null}
                    </div>
                    <div className="mt-3 text-xs leading-6 sm:text-sm" style={{ color: COLORS.neutral[600] }}>
                      {item.luckText}
                    </div>
                    <div className="mt-3 text-xs font-medium" style={{ color: COLORS.primary[700] }}>
                      点击查看三才六格
                    </div>
                  </button>
                ))}
              </div>
            ) : null}
          </section>
        ) : null}

        {comboList.length > 0 && showComboListPage ? (
          <section className="mb-8">
            <div className="mb-5 flex items-center justify-between gap-4">
              <div>
                <h3 className={`text-xl font-semibold sm:text-2xl`} style={{ color: COLORS.neutral[800], fontFamily: "'Kaiti','STKaiti','楷体',serif" }}>
                  推荐笔画组合
                </h3>
                <p className="mt-1 text-sm" style={{ color: COLORS.neutral[500] }}>
                  已按 {getGenderLabel(gender)} 口径优先过滤与排序，点击任意组合查看候选字
                </p>
              </div>
              {sortedSavedNames.length > 0 ? (
                <button
                  type="button"
                  onClick={() => setPage('saved')}
                  className="shrink-0 rounded-full border px-4 py-2 text-sm font-medium transition-all duration-200 hover:shadow-sm"
                  style={{ borderColor: COLORS.primary[200], backgroundColor: COLORS.primary[50], color: COLORS.primary[700] }}
                >
                  {`已保存名字 (${sortedSavedNames.length})`}
                </button>
              ) : null}
            </div>
            {saveToast ? (
              <div className="mb-4 text-sm font-medium" style={{ color: COLORS.primary[700] }}>
                {saveToast}
              </div>
            ) : null}
            <div className="grid gap-4 sm:grid-cols-2">
              {comboList.map((combo) => (
                <button
                  key={combo.key}
                  type="button"
                  onClick={() => {
                    setSelectedComboKey(combo.key);
                    setShowAllSecondChars(false);
                    setShowAllThirdChars(false);
                    setSelectedSecondChar(null);
                    setSelectedThirdChar(null);
                    setPage('candidates');
                  }}
                  className={`group relative overflow-hidden rounded-2xl border p-4 text-left transition-all duration-200 hover:shadow-md sm:rounded-3xl sm:p-4 ${selectedCombo?.key === combo.key ? 'ring-2' : ''}`}
                  style={{
                    borderColor: selectedCombo?.key === combo.key ? COLORS.primary[400] : COLORS.neutral[200],
                    backgroundColor: selectedCombo?.key === combo.key ? COLORS.primary[50] : 'white',
                    boxShadow: selectedCombo?.key === combo.key ? `0 8px 24px ${COLORS.primary[200]}40` : undefined,
                    ringColor: selectedCombo?.key === combo.key ? COLORS.primary[400] : undefined,
                  }}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className={`text-xl font-bold sm:text-2xl`} style={{ color: COLORS.neutral[800], fontFamily: "'Kaiti','STKaiti','楷体',serif" }}>
                      {combo.secondStroke} 画 + {combo.thirdStroke} 画
                    </div>
                    <div className={`rounded-full border px-3 py-1 text-xs font-semibold`} style={{
                      borderColor: combo.score >= 90 ? '#D1FAE5' : combo.score >= 80 ? '#BAE6FD' : combo.score >= 70 ? '#FED7AA' : '#FECACA',
                      backgroundColor: combo.score >= 90 ? '#ECFDF5' : combo.score >= 80 ? '#E0F2FE' : combo.score >= 70 ? '#FFF7ED' : '#FEF2F2',
                      color: combo.score >= 90 ? '#059669' : combo.score >= 80 ? '#0284C7' : combo.score >= 70 ? '#EA580C' : '#DC2626',
                    }}>
                      {combo.score} 分
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    {combo.tags.slice(0, 3).map((tag) => (
                      <span
                        key={`${combo.key}-${tag}`}
                        className="rounded-full border px-2.5 py-1 text-xs"
                        style={{ borderColor: COLORS.primary[100], backgroundColor: 'rgba(255,255,255,0.8)', color: COLORS.primary[700] }}
                      >
                        {tag}
                      </span>
                    ))}
                    <span className="rounded-full border px-2.5 py-1 text-xs" style={{ borderColor: COLORS.neutral[200], backgroundColor: COLORS.neutral[50], color: COLORS.neutral[600] }}>
                      三才 {combo.threeTalents.sequence}
                    </span>
                  </div>
                  <div className="mt-3 border-t pt-3 text-[11px]" style={{ borderColor: COLORS.neutral[100], color: COLORS.neutral[500] }}>
                    <div className="flex flex-wrap gap-2">
                      {COMBO_GRID_ORDER.map(({ key, label }) => {
                        const grid = combo.grids[key];
                        const tone = getGenderLuckColor(grid.genderLuck, gender);
                        return (
                          <span
                            key={key}
                            className="rounded-full border px-2.5 py-1"
                            style={{ borderColor: tone.border, backgroundColor: tone.bg, color: tone.text }}
                          >
                            {label}{formatGridNumber(grid)} {formatNamingMasterGenderLuck(grid.genderLuck)}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </section>
        ) : null}

        {showSavedPage ? (
          <section className="mb-8">
            <div className="mb-5 flex items-center justify-between gap-4">
              <div>
                <h3 className={`text-xl font-semibold sm:text-2xl`} style={{ color: COLORS.neutral[800], fontFamily: "'Kaiti','STKaiti','楷体',serif" }}>
                  已保存名字
                </h3>
                <p className="mt-1 text-sm" style={{ color: COLORS.neutral[500] }}>
                  按综合评分从高到低排序，点击任意名字可直接跳到三才六格。
                </p>
              </div>
            </div>
            {sortedSavedNames.length > 0 ? (
              <div className="grid gap-4 sm:grid-cols-2">
                {sortedSavedNames.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => onOpenSancaiLiuge?.({
                      surname: item.surname,
                      givenName: `${item.secondChar.char}${item.thirdChar.char}`,
                      gender: item.gender,
                    })}
                    className="overflow-hidden rounded-2xl border bg-white p-4 text-left shadow-[0_4px_16px_rgba(107,61,31,0.05)] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_10px_24px_rgba(107,61,31,0.10)] sm:rounded-3xl sm:p-5"
                    style={{ borderColor: COLORS.neutral[200] }}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className={`text-2xl font-bold sm:text-3xl`} style={{ color: COLORS.neutral[800], fontFamily: "'Kaiti','STKaiti','楷体',serif" }}>
                          {item.fullName}
                        </div>
                        <div className="mt-2 flex flex-wrap items-center gap-2 text-sm" style={{ color: COLORS.neutral[600] }}>
                          <span className="rounded-full border px-2.5 py-1 text-xs" style={{ borderColor: COLORS.neutral[200], backgroundColor: COLORS.neutral[50] }}>
                            {getGenderLabel(item.gender)}
                          </span>
                          <span className="rounded-full border px-2.5 py-1 text-xs" style={{ borderColor: COLORS.primary[200], backgroundColor: COLORS.primary[50], color: COLORS.primary[700] }}>
                            三才 {item.combo.threeTalents.sequence}
                          </span>
                          <span className="rounded-full border px-2.5 py-1 text-xs" style={{ borderColor: COLORS.neutral[200], backgroundColor: COLORS.neutral[50] }}>
                            {item.secondChar.char} {item.secondChar.stroke}画 · {item.thirdChar.char} {item.thirdChar.stroke}画
                          </span>
                        </div>
                      </div>
                      <div className="rounded-full border px-4 py-2 text-sm font-semibold" style={{
                        borderColor: item.combo.score >= 90 ? '#D1FAE5' : item.combo.score >= 80 ? '#BAE6FD' : item.combo.score >= 70 ? '#FED7AA' : '#FECACA',
                        backgroundColor: item.combo.score >= 90 ? '#ECFDF5' : item.combo.score >= 80 ? '#E0F2FE' : item.combo.score >= 70 ? '#FFF7ED' : '#FEF2F2',
                        color: item.combo.score >= 90 ? '#059669' : item.combo.score >= 80 ? '#0284C7' : item.combo.score >= 70 ? '#EA580C' : '#DC2626',
                      }}>
                        {item.combo.score} 分
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2 text-xs">
                      {COMBO_GRID_ORDER.map(({ key, label }) => {
                        const grid = item.combo.grids[key];
                        const tone = getGenderLuckColor(grid.genderLuck, item.gender);
                        return (
                          <span
                            key={`${item.id}-${key}`}
                            className="rounded-full border px-2.5 py-1"
                            style={{ borderColor: tone.border, backgroundColor: tone.bg, color: tone.text }}
                          >
                            {label}{formatGridNumber(grid)} {formatNamingMasterGenderLuck(grid.genderLuck)}
                          </span>
                        );
                      })}
                    </div>
                    <div className="mt-3 text-sm leading-relaxed" style={{ color: COLORS.neutral[600] }}>
                      {item.combo.threeTalents.summary}
                    </div>
                    <div className="mt-3 text-xs font-medium" style={{ color: COLORS.primary[700] }}>
                      点击查看三才六格
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="rounded-2xl border bg-white p-5 text-sm" style={{ borderColor: COLORS.neutral[200], color: COLORS.neutral[500] }}>
                还没有已保存名字，先去候选字页选中二字和三字后点击对号保存。
              </div>
            )}
          </section>
        ) : null}

        {selectedCombo && showCandidatePage ? (
          <section className="space-y-6">
            <div className="bg-transparent p-0 shadow-none">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                <div className={`text-2xl font-bold sm:text-3xl`} style={{ color: COLORS.neutral[800], fontFamily: "'Kaiti','STKaiti','楷体',serif" }}>
                  {resolvedSurname?.surname || surname}
                </div>
                <GenderToggle value={gender} onChange={handleGenderChange} />
                <span className="rounded-full border px-3 py-1 text-sm font-medium" style={{ borderColor: COLORS.primary[200], backgroundColor: COLORS.primary[50], color: COLORS.primary[700] }}>
                  {resolvedSurname?.stroke || 0}+{selectedCombo.secondStroke}+{selectedCombo.thirdStroke}
                </span>
              </div>
            </div>

            <div className="grid gap-6 xl:grid-cols-2">
              <div className="xl:col-span-2 overflow-hidden rounded-2xl border bg-white p-4 shadow-[0_4px_16px_rgba(107,61,31,0.05)] sm:rounded-3xl sm:p-5" style={{ borderColor: COLORS.neutral[200] }}>
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <div className="text-sm font-medium" style={{ color: COLORS.neutral[600] }}>组合名字</div>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-lg sm:text-xl" style={{ color: COLORS.neutral[800], fontFamily: "'Kaiti','STKaiti','楷体',serif" }}>
                      <span>{resolvedSurname?.surname || surname}</span>
                      <span className="inline-flex min-w-[2.5rem] items-center justify-center rounded-xl border px-3 py-1" style={{ borderColor: selectedSecondChar ? COLORS.primary[300] : COLORS.neutral[200], backgroundColor: selectedSecondChar ? COLORS.primary[50] : COLORS.neutral[50], color: selectedSecondChar ? COLORS.primary[700] : COLORS.neutral[400] }}>
                        {selectedSecondChar?.char || '二字'}
                      </span>
                      <span className="inline-flex min-w-[2.5rem] items-center justify-center rounded-xl border px-3 py-1" style={{ borderColor: selectedThirdChar ? COLORS.primary[300] : COLORS.neutral[200], backgroundColor: selectedThirdChar ? COLORS.primary[50] : COLORS.neutral[50], color: selectedThirdChar ? COLORS.primary[700] : COLORS.neutral[400] }}>
                        {selectedThirdChar?.char || '三字'}
                      </span>
                    </div>
                    <div className="mt-2 text-sm" style={{ color: COLORS.neutral[500] }}>
                      先选第二字，再选第三字；选好后点击右侧对号保存为名字卡片。
                    </div>
                    {saveToast ? (
                      <div className="mt-2 text-sm font-medium" style={{ color: COLORS.primary[700] }}>
                        {saveToast}
                      </div>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    disabled={!selectedSecondChar || !selectedThirdChar}
                    onClick={handleSaveComposedName}
                    className="inline-flex h-12 w-12 items-center justify-center rounded-full text-white transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-40"
                    style={{ backgroundColor: COLORS.primary[500], boxShadow: '0 8px 20px rgba(166, 93, 46, 0.28)' }}
                  >
                    <Check className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <CandidateGrid
                title="第二字候选"
                stroke={selectedCombo.secondStroke}
                total={secondCandidatesAll.length}
                candidates={secondCandidates}
                showAll={showAllSecondChars}
                onToggleShowAll={() => setShowAllSecondChars((value) => !value)}
                surname={resolvedSurname?.surname || surname}
                comboKey={selectedCombo.key}
                gender={gender}
                view={page}
                onOpenKangxiDictionary={onOpenKangxiDictionary}
                selectedChar={selectedSecondChar?.char}
                onSelectChar={handleSelectSecondChar}
              />
              <CandidateGrid
                title="第三字候选"
                stroke={selectedCombo.thirdStroke}
                total={thirdCandidatesAll.length}
                candidates={thirdCandidates}
                showAll={showAllThirdChars}
                onToggleShowAll={() => setShowAllThirdChars((value) => !value)}
                surname={resolvedSurname?.surname || surname}
                comboKey={selectedCombo.key}
                gender={gender}
                view={page}
                onOpenKangxiDictionary={onOpenKangxiDictionary}
                selectedChar={selectedThirdChar?.char}
                onSelectChar={handleSelectThirdChar}
              />
            </div>

            <details open className="group overflow-hidden rounded-2xl border bg-white p-4 shadow-[0_4px_16px_rgba(107,61,31,0.05)] sm:rounded-3xl sm:p-5" style={{ borderColor: COLORS.neutral[200] }}>
              <summary className="flex cursor-pointer list-none items-center justify-between gap-3 [&::-webkit-details-marker]:hidden">
                <div>
                  <div className="text-xs font-medium uppercase tracking-wider sm:text-sm" style={{ color: COLORS.neutral[500] }}>数理详情</div>
                  <div className={`mt-1 text-xl font-semibold sm:text-2xl`} style={{ color: COLORS.neutral[800], fontFamily: "'Kaiti','STKaiti','楷体',serif" }}>三才与六格</div>
                </div>
                <ChevronRight className="h-5 w-5 transition group-open:rotate-90" style={{ color: COLORS.primary[500] }} />
              </summary>

              <div className="mt-5 border-t pt-5" style={{ borderColor: COLORS.neutral[100] }}>
                <div className="mb-4 overflow-hidden rounded-xl border p-4 sm:rounded-2xl sm:p-5" style={{ borderColor: COLORS.primary[200], backgroundColor: COLORS.primary[50] }}>
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <div className="text-sm font-medium" style={{ color: COLORS.neutral[600] }}>综合评分</div>
                      <div className="mt-1 flex items-baseline gap-2">
                        <span className="text-3xl font-bold sm:text-4xl" style={{ color: COLORS.primary[600] }}>{selectedCombo.score}</span>
                        <span className="text-base" style={{ color: COLORS.neutral[500] }}>分</span>
                      </div>
                    </div>
                    <div className="rounded-full border px-4 py-2 text-sm font-semibold" style={{
                      borderColor: selectedCombo.score >= 90 ? '#D1FAE5' : selectedCombo.score >= 80 ? '#BAE6FD' : selectedCombo.score >= 70 ? '#FED7AA' : '#FECACA',
                      backgroundColor: selectedCombo.score >= 90 ? '#ECFDF5' : selectedCombo.score >= 80 ? '#E0F2FE' : selectedCombo.score >= 70 ? '#FFF7ED' : '#FEF2F2',
                      color: selectedCombo.score >= 90 ? '#059669' : selectedCombo.score >= 80 ? '#0284C7' : selectedCombo.score >= 70 ? '#EA580C' : '#DC2626',
                    }}>
                      {selectedCombo.score >= 90 ? '极佳' : selectedCombo.score >= 80 ? '优秀' : selectedCombo.score >= 70 ? '良好' : '一般'}
                    </div>
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-6">
                    {COMBO_GRID_ORDER.map(({ key, label }) => {
                      const grid = selectedCombo.grids[key];
                      const tone = getGenderLuckColor(grid.genderLuck, gender);
                      return (
                        <div key={key} className="text-center">
                          <div className="text-xs" style={{ color: COLORS.neutral[500] }}>{label}</div>
                          <div className="text-base font-semibold" style={{ color: COLORS.neutral[700] }}>{formatGridNumber(grid)}</div>
                          <div className="text-xs" style={{ color: tone.text }}>{formatNamingMasterGenderLuck(grid.genderLuck)}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="mb-4 flex flex-wrap items-center gap-2 text-sm" style={{ color: COLORS.neutral[600] }}>
                  <span className="rounded-full border px-2.5 py-1 text-xs" style={{ borderColor: COLORS.neutral[200], backgroundColor: COLORS.neutral[50] }}>三才</span>
                  <span className="font-medium">{selectedCombo.threeTalents.sequence}</span>
                  <span className="text-xs" style={{ color: COLORS.neutral[400] }}>·</span>
                  <span>天人格 {selectedCombo.threeTalents.relationTR}</span>
                  <span className="text-xs" style={{ color: COLORS.neutral[400] }}>·</span>
                  <span>人格地格 {selectedCombo.threeTalents.relationRD}</span>
                </div>
                  <div className="mb-4 text-sm leading-relaxed" style={{ color: COLORS.neutral[600] }}>
                    {selectedCombo.threeTalents.summary}
                  </div>

                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {COMBO_GRID_ORDER.map(({ key, label }) => {
                    const grid = selectedCombo.grids[key];
                    const tone = getGenderLuckColor(grid.genderLuck, gender);
                    return (
                      <div key={key} className="rounded-xl border p-3 transition-all duration-200 hover:shadow-sm sm:rounded-2xl sm:p-4" style={{ borderColor: COLORS.neutral[200], backgroundColor: COLORS.neutral[50] }}>
                        <div className="flex items-center justify-between gap-3">
                          <span className={`text-lg font-semibold sm:text-xl`} style={{ color: COLORS.neutral[700], fontFamily: "'Kaiti','STKaiti','楷体',serif" }}>{label}</span>
                          <span className="rounded-full border px-2.5 py-1 text-xs font-medium" style={{
                            borderColor: grid.score81 >= 90 ? '#D1FAE5' : grid.score81 >= 80 ? '#BAE6FD' : grid.score81 >= 70 ? '#FED7AA' : '#FECACA',
                            backgroundColor: grid.score81 >= 90 ? '#ECFDF5' : grid.score81 >= 80 ? '#E0F2FE' : grid.score81 >= 70 ? '#FFF7ED' : '#FEF2F2',
                            color: grid.score81 >= 90 ? '#059669' : grid.score81 >= 80 ? '#0284C7' : grid.score81 >= 70 ? '#EA580C' : '#DC2626',
                          }}>
                            {grid.chartLuck}
                          </span>
                        </div>
                        <div className="mt-2 text-2xl font-bold sm:text-3xl" style={{ color: COLORS.primary[600] }}>{formatGridNumber(grid)}</div>
                        <div className="mt-1 text-xs" style={{ color: COLORS.neutral[500] }}>
                          {grid.wuxing} · {grid.meaning81}
                        </div>
                        <div
                          className="mt-2 inline-flex rounded-full border px-2.5 py-1 text-[11px] font-medium"
                          style={{ borderColor: tone.border, backgroundColor: tone.bg, color: tone.text }}
                        >
                          {formatNamingMasterGenderLuck(grid.genderLuck)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </details>

            <details open className="group overflow-hidden rounded-2xl border bg-white p-5 shadow-[0_4px_20px_rgba(107,61,31,0.06)] sm:rounded-3xl sm:p-6" style={{ borderColor: COLORS.neutral[200] }}>
              <summary className="flex cursor-pointer list-none items-center justify-between gap-3 [&::-webkit-details-marker]:hidden">
                <div>
                  <div className="text-xs font-medium uppercase tracking-wider sm:text-sm" style={{ color: COLORS.neutral[500] }}>阶段运势</div>
                  <div className={`mt-1 text-xl font-semibold sm:text-2xl`} style={{ color: COLORS.neutral[800], fontFamily: "'Kaiti','STKaiti','楷体',serif" }}>人生各阶段</div>
                </div>
                <ChevronRight className="h-5 w-5 transition group-open:rotate-90" style={{ color: COLORS.primary[500] }} />
              </summary>

              <div className="mt-5 grid gap-4 border-t pt-5 sm:grid-cols-2" style={{ borderColor: COLORS.neutral[100] }}>
                {selectedCombo.stages.map((stage) => (
                  <div key={stage.key} className="rounded-xl border p-4 sm:rounded-2xl" style={{ borderColor: COLORS.neutral[200], backgroundColor: COLORS.neutral[50] }}>
                    <div className="flex items-center justify-between gap-3">
                      <span className={`text-base font-semibold sm:text-lg`} style={{ color: COLORS.neutral[700], fontFamily: "'Kaiti','STKaiti','楷体',serif" }}>{stage.label}</span>
                      <span className="rounded-full border px-2.5 py-1 text-xs font-medium" style={{
                        borderColor: stage.level === '大吉' ? '#D1FAE5' : stage.level === '吉' ? '#BAE6FD' : stage.level === '平' ? '#E7E5E4' : stage.level === '小凶' ? '#FED7AA' : '#FECACA',
                        backgroundColor: stage.level === '大吉' ? '#ECFDF5' : stage.level === '吉' ? '#E0F2FE' : stage.level === '平' ? '#F5F5F4' : stage.level === '小凶' ? '#FFF7ED' : '#FEF2F2',
                        color: stage.level === '大吉' ? '#059669' : stage.level === '吉' ? '#0284C7' : stage.level === '平' ? '#78716C' : stage.level === '小凶' ? '#EA580C' : '#DC2626',
                      }}>
                        {stage.level}
                      </span>
                    </div>
                    <div className="mt-2 text-sm leading-relaxed" style={{ color: COLORS.neutral[600] }}>{stage.summary}</div>
                  </div>
                ))}
              </div>
            </details>

            {onOpenKangxiDictionary ? (
              <div className="rounded-[22px] border border-[#E9DACA] bg-white/88 p-4 text-[12px] leading-6 text-[#7D6555] shadow-[0_8px_22px_rgba(102,63,35,0.05)] sm:rounded-[24px] sm:p-5 sm:text-sm sm:leading-7 sm:shadow-[0_10px_28px_rgba(102,63,35,0.06)]">
                <div className="flex items-start gap-3">
                  <BookOpen className="mt-0.5 h-5 w-5 text-[#B56A37]" />
                  <div>
                    候选字页面已先做“常用名字符 + 笔画 + 五行”筛选，但它仍不替你最终定名。若想继续核对某个字的姓名学笔画、字典释义、
                    部首、拼音与异体，可以直接双击字块，跳转到项目里的“康熙字典”页继续看单字详情。
                  </div>
                </div>
              </div>
            ) : null}
          </section>
        ) : null}
      </div>
    </div>
  );
};

export default NamingMasterApp;
